import { useEffect, useRef, useState } from 'react'

import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import LoadSpinner from '~/components/atoms/LoadSpinner/LoadSpinner'
import SearchInifiniteLoader from '~/components/atoms/SearchInfiniteLoader/SearchInfiniteLoader'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { getTitles } from '~/components/blocks/SearchDialogInner/resultTypeMapper'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { addTranslations, type Locale } from '~/utils/i18n'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/search.module.css'
import RecordTypes = indexes.RecordTypes
import classNames from 'classnames'

import PageSubMenu from '~/components/atoms/PageSubMenu/PageSubMenu'
import useSearchAnalytics from '~/hooks/useSearchAnalytics'
import { buildBreadcrumb, getBreadcrumbJsonLd } from '~/utils/breadcrumb'

const searchComponents = [
  {
    total: 0,
    searchType: 'artist',
  },
  {
    total: 0,
    searchType: 'artwork',
  },
  {
    total: 0,
    searchType: 'collection',
  },
  {
    total: 0,
    searchType: 'article',
  },
  {
    total: 0,
    searchType: 'podcast',
  },
  {
    total: 0,
    searchType: 'video',
  },
]

const artworksEvents = {
  artwork: {
    method: SearchAnalystMethods.clickedObjectIDsAfterSearch,
    name: SearchAnalystEventNames.plpArtworkClicked,
  },
  artist: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.plpArtworkArtistClicked,
  },
}

export interface PageData {
  breadcrumb: BreadcrumbProps['list']
}

const ArtworksSearchPage: NextPage<PageData> = (props: PageData) => {
  const { breadcrumb } = props
  const { query } = useRouter()
  const t = useTranslations('searchPage')
  const tSearchResultTypes = useTranslations('searchResultTypes')
  const [totalResults, setTotalResults] = useState<number>(0)
  const [searchTerm, setSearchTerm] = useState<string | undefined>()
  const [loading, setLoading] = useState<boolean>(true)
  const [routerReady, setRouterReady] = useState(false)
  const [searchComponent] = useState(searchComponents)

  const resultsCounts = useRef(0)

  const [filter, setFilter] = useState<string | null>(null)
  const { handleSearchPageLoad } = useSearchAnalytics()
  const prevSearchTermRef = useRef<null | string | undefined>(null)
  const prevQueryRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    setRouterReady(true)

    // Check if query.q has changed
    if (query.q && prevQueryRef.current !== query.q) {
      // Reset filter when search query changes
      setFilter(null)
      setSearchTerm(query.q as string)
      prevQueryRef.current = query.q as string
    } else if (!query.q) {
      prevQueryRef.current = undefined
    }
  }, [query.q])

  const analyticsTimeoutRef = useRef<any>(null)
  const hasSearchChangedRef = useRef<any>(false)

  const onResultsLoadHandler = async (type: string, number: number) => {
    searchComponent.map((component: any) => {
      if (component.searchType === type) {
        component.total = number
        resultsCounts.current += 1
      }
    })

    if (resultsCounts.current >= searchComponent.length) {
      let total = 0
      searchComponent.map((result: any) => {
        total += result.total
      })
      setTotalResults(total)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (searchTerm !== prevSearchTermRef.current) {
      if (analyticsTimeoutRef.current) {
        clearTimeout(analyticsTimeoutRef.current)
      }

      // Reset counters
      resultsCounts.current = 0
      prevSearchTermRef.current = searchTerm
      hasSearchChangedRef.current = true

      analyticsTimeoutRef.current = setTimeout(() => {
        if (searchTerm) {
          handleSearchPageLoad(searchTerm, {
            numArtistResults:
              searchComponent.find(x => x.searchType === RecordTypes.ARTIST)
                ?.total || 0,
            numArtworkResults:
              searchComponent.find(x => x.searchType === RecordTypes.ARTWORK)
                ?.total || 0,
            numCollectionResults:
              searchComponent.find(x => x.searchType === RecordTypes.COLLECTION)
                ?.total || 0,
            numArticleResults:
              searchComponent.find(x => x.searchType === RecordTypes.ARTICLES)
                ?.total || 0,
          })
        }

        // Clear the timeout ref
        analyticsTimeoutRef.current = null
        hasSearchChangedRef.current = false
      }, 4000)
    }
  }, [searchTerm, handleSearchPageLoad])

  const pageLinks = [
    ...(searchComponent
      ?.filter(component => component.total > 0)
      .map((component: any) => ({
        onClickHandler: () => setFilter(component.searchType),
        isActive: filter === component.searchType,
        label: `${getTitles(component.searchType, tSearchResultTypes)} (${component.total})`,
      })) || []),
  ]

  return (
    <div className={styles.searchPage}>
      {breadcrumb && <Breadcrumb list={breadcrumb} />}
      <Fixture verticalPadding={false} className={styles.searchPageFixture}>
        {searchTerm && (
          <>
            <Container gutters={gutterTypes.always}>
              <div className={styles.searchHeader}>
                {loading && <LoadSpinner />}
                <Text
                  size={TextSizes.bodyMed}
                  Tag="p"
                  dataNoSnippet={true}
                  className={styles.resultIntroText}
                >
                  {t('searchResultsFor')}
                </Text>
                <Text
                  size={TextSizes.primaryTitleMed}
                  Tag="h1"
                  dataNoSnippet={true}
                  className={styles.resultText}
                >
                  <em className={styles.firstSpeech}>"</em>
                  {searchTerm}
                  <em className={styles.lastSpeech}>"</em>{' '}
                  {!loading && <span>({totalResults})</span>}
                </Text>
              </div>
            </Container>

            {/* Sub menu */}
            {!loading && (
              <div
                className={classNames(styles.pageSubMenu, {
                  [styles.pushDown]: filter === null,
                })}
              >
                <PageSubMenu pageLinks={pageLinks} />
              </div>
            )}

            {/* Main content search */}
            <Container gutters={gutterTypes.always}>
              <>
                {loading && <LoadSpinner />}

                {/* <div className={styles.filterLozenges}>
                {searchComponents &&
                  searchComponents.map((component: any) => {
                    if (component.total > 0)
                      return (
                        <Lozenge
                          key={component.searchType}
                          hideOnZero
                          title={`${getTitles(component.searchType)} (${
                            component.total
                          })`}
                          isActive={filter === component.searchType}
                          onClick={() => setFilter(component.searchType)}
                        />
                      )
                  })}
              </div> */}

                {routerReady && (
                  <>
                    {(!filter || filter === 'artwork') && (
                      <SearchInifiniteLoader
                        recordType={RecordTypes.ARTWORK}
                        searchTerm={searchTerm}
                        typeSelection={filter}
                        columns="col-1/2--sm col-1/3--md col-1/4--hg"
                        onResultsLoad={onResultsLoadHandler}
                        searchInsightsEvents={artworksEvents}
                      />
                    )}
                    {(!filter || filter === 'artist') && (
                      <SearchInifiniteLoader
                        recordType={RecordTypes.ARTIST}
                        searchTerm={searchTerm}
                        columns="col-1 col-1/4--sm col-1/4--md col-1/6--lg col-1/6--hg"
                        pageSize={8}
                        onResultsLoad={onResultsLoadHandler}
                        typeSelection={filter}
                        searchInsightsEvent={{
                          method: SearchAnalystMethods.clickedFilters,
                          name: SearchAnalystEventNames.plpArtistClicked,
                        }}
                      />
                    )}
                    {(!filter || filter === 'collection') && (
                      <SearchInifiniteLoader
                        recordType={RecordTypes.COLLECTION}
                        searchTerm={searchTerm}
                        typeSelection={filter}
                        // columns="col-1/3 col-1/4--sm col-1/4--md col-1/8--lg  col-1/8--hg"
                        columns="col-1 col-1/4--sm col-1/4--md col-1/6--lg col-1/6--hg"
                        pageSize={8}
                        onResultsLoad={onResultsLoadHandler}
                        searchInsightsEvent={{
                          method: SearchAnalystMethods.clickedFilters,
                          name: SearchAnalystEventNames.plpCollectionClicked,
                        }}
                      />
                    )}

                    {(!filter || filter === 'article') && (
                      <SearchInifiniteLoader
                        searchTerm={searchTerm}
                        typeSelection={filter}
                        recordType={RecordTypes.ARTICLES}
                        onResultsLoad={onResultsLoadHandler}
                        columns="col-1/2--sm col-1/4--lg col-1/4--hg"
                        searchInsightsEvent={{
                          method:
                            SearchAnalystMethods.clickedObjectIDsAfterSearch,
                          name: SearchAnalystEventNames.plpArticleClicked,
                        }}
                      />
                    )}

                    {(!filter || filter === 'podcast') && (
                      <SearchInifiniteLoader
                        searchTerm={searchTerm}
                        typeSelection={filter}
                        recordType={RecordTypes.PODCASTS}
                        onResultsLoad={onResultsLoadHandler}
                        columns="col-1/2--sm col-1/4--lg col-1/4--hg"
                        searchInsightsEvent={{
                          method:
                            SearchAnalystMethods.clickedObjectIDsAfterSearch,
                          name: SearchAnalystEventNames.plpArticleClicked,
                        }}
                      />
                    )}
                    {(!filter || filter === 'video') && (
                      <SearchInifiniteLoader
                        searchTerm={searchTerm}
                        typeSelection={filter}
                        recordType={RecordTypes.VIDEOS}
                        onResultsLoad={onResultsLoadHandler}
                        columns="col-1/2--sm col-1/4--lg col-1/4--hg"
                        searchInsightsEvent={{
                          method:
                            SearchAnalystMethods.clickedObjectIDsAfterSearch,
                          name: SearchAnalystEventNames.plpArticleClicked,
                        }}
                      />
                    )}
                  </>
                )}
              </>
            </Container>
          </>
        )}
      </Fixture>
    </div>
  )
}

export const getStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}
const getStaticPropsOriginal = async function getStaticProps(context: any) {
  try {
    const lang = (context?.params?.lang as Locale) || 'en'
    const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
      ['search'],
      '',
      undefined,
      undefined,
      lang
    )

    const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

    return {
      props: {
        breadcrumb,
        breadcrumbJsonLd,
        url: '/search',
        hasGoogleReviews: true,
        meta: {
          title: 'MyArtbroker - Search',
        },
      },
    }
  } catch {
    return { props: { notFound: true } }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async context => {
  const result = await getStaticPropsOriginal(context)
  if ('notFound' in result || 'redirect' in result) {
    return result // Return notFound or redirect directly
  }
  return { props: await addTranslations(result.props, context) }
})

export default ArtworksSearchPage
